import { Menu } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { uniqueId } from 'lodash';
import { insert, insertAll, omit, remove, update } from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CategoryInputField } from './CategoryInputField';
import { SortableContainer } from './SortableContainer';
import { UNIQUEID_CATEGORY_PREFIX } from './constants';
import { AddCategoriesFormValues } from './types';

type CategoryInputFieldComponentProps = {
  addTouchedId: (rowId: string) => void;
  canDelete: boolean;
  index: number;
  fieldName: string;
  placeholder: string;
  removeTouchedRowId: (rowId: string) => void;
  rowId: string;
  touched: boolean;
};

const CategoryDraggableContainer = styled(SortableContainer)`
  margin-block-end: 16px;
`;

const useTouchedRowIdSet = () => {
  const [touchedFieldRowId, setTouchedFieldRowId] = useState<string[]>([]);
  return {
    touchedRowId: (rowId: string) => touchedFieldRowId.includes(rowId),
    addTouchedId: (rowId: string) => {
      if (!touchedFieldRowId.includes(rowId)) {
        setTouchedFieldRowId(ids => ids.concat(rowId));
      }
    },
    removeTouchedRowId: (rowId: string) => {
      setTouchedFieldRowId(ids => ids.filter(id => id !== rowId));
    },
  };
};

const buildCategoryValue = (name: string) => ({
  name,
  description: '',
  image: { type: 'no-image' } as const,
  _rowId: uniqueId(UNIQUEID_CATEGORY_PREFIX),
});

const buildChildCategoryValue = (name: string) => ({
  name,
  _rowId: uniqueId(UNIQUEID_CATEGORY_PREFIX),
});

const CategoryInputFieldComponent = ({
  addTouchedId,
  canDelete,
  index,
  fieldName,
  placeholder,
  removeTouchedRowId,
  rowId,
  touched,
}: CategoryInputFieldComponentProps) => {
  const {
    values: { categories, childCategoriesRecord },
  } = useFormState<AddCategoriesFormValues>();
  const form = useForm<AddCategoriesFormValues>();

  const handleBlur = useCallback(() => {
    addTouchedId(rowId);
  }, [addTouchedId, rowId]);

  const handleRemove = useCallback(() => {
    removeTouchedRowId(rowId);

    form.batch(() => {
      form.change('categories', remove(index, 1, categories));
      form.change('childCategoriesRecord', omit([rowId], childCategoriesRecord));
    });
  }, [categories, childCategoriesRecord, form, index, removeTouchedRowId, rowId]);

  const handlePressEnter = useCallback(() => {
    const categoryValue = buildCategoryValue('');

    form.batch(() => {
      form.change('categories', insert(index + 1, categoryValue, categories));
      form.change('childCategoriesRecord', {
        [categoryValue._rowId]: [buildChildCategoryValue('')],
        ...childCategoriesRecord,
      });
    });
  }, [categories, childCategoriesRecord, form, index]);

  const handlePaste = useCallback(
    (categoryNames: string[]): void => {
      const [firstCategoryNames, ...otherCategoryNames] = categoryNames;
      const otherCategoryValues = otherCategoryNames.map(buildCategoryValue);
      const childCategoryValues = otherCategoryValues.reduce<AddCategoriesFormValues['childCategoriesRecord']>(
        (acc, val) => ({ ...acc, [val._rowId]: [buildChildCategoryValue('')] }),
        {},
      );
      const updatedFirstCategoryValue = {
        ...categories[index],
        name: firstCategoryNames,
      };

      form.batch(() => {
        form.change(
          'categories',
          insertAll(index + 1, otherCategoryValues, update(index, updatedFirstCategoryValue, categories)),
        );
        form.change('childCategoriesRecord', {
          ...childCategoriesRecord,
          ...childCategoryValues,
        });
      });
    },
    [categories, childCategoriesRecord, form, index],
  );

  return (
    <CategoryInputField
      touched={touched}
      onBlur={handleBlur}
      onRemove={handleRemove}
      onPressEnter={handlePressEnter}
      onPaste={handlePaste}
      fieldName={fieldName}
      canDelete={canDelete}
      placeholder={placeholder}
    />
  );
};

export const CategoryInputFields = () => {
  const intl = useIntl();
  const { values } = useFormState<AddCategoriesFormValues>();
  const form = useForm<AddCategoriesFormValues>();
  const { addTouchedId, removeTouchedRowId, touchedRowId } = useTouchedRowIdSet();
  const { fields } = useFieldArray<AddCategoriesFormValues>('categories');

  const [draggingInputRowId, setDraggingInputRowId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }));

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDraggingInputRowId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = values.categories.findIndex(category => category._rowId === active.id);
      const newIndex = values.categories.findIndex(category => category._rowId === over.id);

      if (oldIndex === -1 && newIndex === -1) {
        return;
      }

      form.change('categories', arrayMove(values.categories, oldIndex, newIndex));
    },
    [form, values.categories],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingInputRowId(event.active.id);
  }, []);

  const draggingCategory = useMemo(
    () => values.categories.find(category => category._rowId === draggingInputRowId),
    [draggingInputRowId, values.categories],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext
        items={fields.map((_, index) => values.categories[index]._rowId)}
        strategy={verticalListSortingStrategy}
      >
        {fields.map((name, index) => {
          const { _rowId } = values.categories[index];
          return (
            <CategoryDraggableContainer id={_rowId} key={_rowId}>
              <CategoryInputFieldComponent
                addTouchedId={addTouchedId}
                canDelete={fields.length !== 1}
                index={index}
                fieldName={`${name}.name`}
                placeholder={intl.formatMessage({
                  id: 'menu.bulkCreateCategories.addCategory.categoryInputPlaceholder',
                })}
                removeTouchedRowId={removeTouchedRowId}
                rowId={_rowId}
                touched={touchedRowId(_rowId)}
              />
            </CategoryDraggableContainer>
          );
        })}
      </SortableContext>

      <DragOverlay>
        {draggingCategory && (
          <Input
            icon={<Menu />}
            value={draggingCategory.name}
            readOnly
            placeholder={intl.formatMessage({
              id: 'menu.bulkCreateCategories.addCategory.categoryInputPlaceholder',
            })}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};
